import React, { useEffect, useMemo, useState } from 'react';
import { UserLocalStorageKeysEnum, UserModel } from '../../../interfaces/user.interface';
import { AppDispatch, RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ClassroomSelector, { ClassroomSelectorOption } from './ClassroomSelector';
import Modal from '../../../components/Common/modal/Modal';
import ClassroomSetupWizard from './ClassroomSetupWizard';
import { getOnboardingData } from '../services/Teacher.service';
import {
  setActiveClassroom,
  setActiveClassroomProject,
  setIsOnboarding,
  setNewClassroomFlow,
  setShowAddNewStudentsPopup,
  setShowPublishingTimelinePopup,
} from '../../../store/userSlice';
import { getTeacherClassroomProjects, getTeacherClassrooms } from '../../../store/thunks/user-teacher.thunk';
import { TeacherOnboardingPagesEnum } from '../TeacherOnboarding';
import { getURLSearchParams } from '../../../helper/helper';
import StudentsAddNewItems from './StudentsAddNewItems';
import { TeacherClassroomStudent } from '../pages/TeacherClassroomStudents';
import * as StudentService from '../services/Student.service';
import { CONFIG } from '../../../config/mainConfig';
import { Toast } from '../../../components/Common/Toast';
import InformNotificationPopup from '../../../components/Common/InformNotificationPopup';
import OnboardingSuccess from './OnboardingSuccess';

export interface MainTeacherControlBarProps {
  user: UserModel | null;
}

const MainTeacherControlBar = ({ user }: MainTeacherControlBarProps) => {
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const navigateTo = useNavigate();
  const searchParams = getURLSearchParams(true);

  const [showAddNewClassroom, setShowAddNewClassroom] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [addNewStudentsLoader, setAddNewStudentsLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showStudentLimitPopup, setShowStudentLimitPopup] = useState<boolean>(false);
  const [showCelebrationPopup, setShowCelebrationPopup] = useState<boolean>(false);

  const [students, setStudents] = useState<TeacherClassroomStudent[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<TeacherClassroomStudent | null>(null);
  const [currentStudentsLimit, setCurrentStudentsLimit] = useState<number | null>(null);
  const [isImportingStudents, setIsImportingStudents] = useState<boolean>(false);

  const currentPathname = useSelector((state: RootState) => state.app.currentPathname);
  const currentSearchQuery = useSelector((state: RootState) => state.app.currentSearchQuery);
  const classrooms = useSelector((state: RootState) => state.user.classrooms);

  const activeClassroomProjects = useSelector((state: RootState) => state.user.activeClassroomProjects);
  const activeClassroom = useSelector((state: RootState) => state.user.activeClassroom);
  const activeClassroomProject = useSelector((state: RootState) => state.user.activeClassroomProject);
  const isOnboarding = useSelector((state: RootState) => state.user.isOnboarding);
  const showAddNewStudentsPopup = useSelector((state: RootState) => state.user.showAddNewStudentsPopup);
  const limitClassroomStudents = useSelector((state: RootState) => state.user.limitClassroomStudents);

  const selectActiveClassroom = (classroomId: string | null) => {
    if (!classrooms?.length) {
      return;
    }
    const classroom = classrooms?.find((classroom) => classroom.id === classroomId);
    if (classroom && classroomId) {
      dispatch(setActiveClassroom({ classroom }));
      localStorage.setItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_ID, classroomId);
      dispatch(getTeacherClassroomProjects(classroomId));
    } else {
      const defaultClassroom = classrooms && classrooms[0];
      if (defaultClassroom) {
        dispatch(setActiveClassroom({ classroom: defaultClassroom }));
        localStorage.setItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_ID, defaultClassroom.id);
        dispatch(getTeacherClassroomProjects(defaultClassroom.id));
      }
    }
  };

  const selectActiveClassroomProject = (classroomProjectId: string | null) => {
    if (!activeClassroomProjects?.length) {
      return;
    }
    const project = activeClassroomProjects?.find((classroom) => classroom.id === classroomProjectId);
    if (project && classroomProjectId) {
      dispatch(setActiveClassroomProject({ project }));
      localStorage.setItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_PROJECT_ID, classroomProjectId);
    } else {
      // Prioritize default project, then first project
      const defaultClassroomProject = activeClassroomProjects.find((p) => p.isDefaultProject);
      const fallbackProject = activeClassroomProjects[0];
      const selectedProject = defaultClassroomProject || fallbackProject;

      if (selectedProject?.id) {
        dispatch(setActiveClassroomProject({ project: selectedProject }));
        localStorage.setItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_PROJECT_ID, selectedProject.id);
      }
    }
  };

  const fetchStudents = async (selectedStudentId?: string) => {
    if (!activeClassroom?.id || !activeClassroomProject?.id) return;

    setLoading(true);
    try {
      const response = await StudentService.getClassroomProjectStudents(
        activeClassroom?.id,
        activeClassroomProject?.id,
      );

      if (response && response.students) {
        // Transform API response to match component's data structure
        const formattedStudents = response.students.map((student: any) => ({
          id: student.email, // Using email as ID since that's what the API uses
          username: student.studentName || student.firstName,
          loginName: student.loginName,
          password: '', // We'll fetch this separately when needed
          isOpen: false,
          bookBalance: student.booksCreateLimit - (student.booksCreatedCounter || 0),
          booksCompleted: student.booksCreatedCounter,
          preOrderStudentBookCount: student.preOrderStudentBookCount,
          bookOrdered: student.bookOrdered,
          parentReponded: !!student.parentReponded,
        }));

        setStudents(formattedStudents);
        if (selectedStudentId) {
          const foundStudent = formattedStudents.find((student: any) => student.id === selectedStudentId);
          setSelectedStudent(foundStudent);
        }
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  };

  const createNewStudents = async (names: string[], skipped?: boolean) => {
    if (!activeClassroom?.id) return;

    try {
      setAddNewStudentsLoader(true);
      const response = await StudentService.createStudents(activeClassroom?.id, names, !!skipped, isOnboarding, () => {
        setShowStudentLimitPopup(true);
      });

      if (response && response.students) {
        // Add new students to the state
        const newStudents = response.students.map((item: any) => ({
          id: item.student.email,
          username: item.student.studentName || item.student.firstName,
          loginName: item.student.loginName,
          password: item.password, // We get the password directly from bulk creation
          isOpen: false,
          bookBalance: item.student.booksCreateLimit - (item.student.booksCreatedCounter || 0),
          booksCompleted: item.student.booksCreatedCounter,
          preOrderStudentBookCount: item.student.preOrderStudentBookCount,
          bookOrdered: item.student.bookOrdered,
          parentReponded: !!item.student.parentReponded,
        }));

        setStudents([...students, ...newStudents]);
        dispatch(setShowAddNewStudentsPopup(false));
        if (isOnboarding) {
          navigateTo(`/classroom/students?onboarding=true&showCelebration=true`);
          setShowCelebrationPopup(true);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setAddNewStudentsLoader(false);
    }
  };

  const finishOnboarding = async () => {
    if (!activeClassroom?.id) return;

    try {
      await StudentService.finishOnboarding(activeClassroom?.id);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (isOnboarding && currentPathname === '/classroom/library') {
      getOnboardingData(dispatch);
      setShowAddNewClassroom(true);
    }
    if (classrooms && classrooms.length === 0) {
      setShowAddNewClassroom(true);
    }
    if ((classrooms?.length || -1) > 0) {
      const storageClassroomId = localStorage.getItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_ID);
      selectActiveClassroom(storageClassroomId);
    }
  }, [classrooms]);

  useEffect(() => {
    if ((activeClassroomProjects?.length || -1) > 0) {
      const storageClassroomProjectId = localStorage.getItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_PROJECT_ID);
      selectActiveClassroomProject(storageClassroomProjectId);
    }
  }, [activeClassroomProjects]);

  // Fetch students on component mount
  useEffect(() => {
    if (activeClassroomProject?.id) {
      fetchStudents();
    }
  }, [activeClassroomProject]);

  useEffect(() => {
    if (showAddNewStudentsPopup && limitClassroomStudents > 0) {
      setCurrentStudentsLimit(limitClassroomStudents - students.length);
    }
  }, [showAddNewStudentsPopup, limitClassroomStudents, students]);

  useEffect(() => {
    const queryParams = new URLSearchParams(currentSearchQuery);
    const isOnboarding = queryParams.get('onboarding') === 'true';
    dispatch(setIsOnboarding(isOnboarding));

    if (queryParams.get('addClassroom') === 'true') {
      getOnboardingData(dispatch);
      setShowAddNewClassroom(true);
      navigateTo(currentPathname || '/classroom/dashboard', { replace: true });
    }

    if (activeClassroomProject?.id) {
      const isSetProjectStart = queryParams.get('setProjectStart') === 'true';
      if (isSetProjectStart) {
        dispatch(setNewClassroomFlow(false));
        dispatch(setShowPublishingTimelinePopup({ projectId: activeClassroomProject?.id, openAddStudentsPopup: true }));
      }
      if (queryParams.get('showCelebration') === 'true') {
        setShowCelebrationPopup(true);
      }
    }
  }, [activeClassroomProject, currentSearchQuery]);

  const isActiveRoute = (path: string): boolean => currentPathname?.includes(path);

  const navigationLinks = [
    { to: '/classroom/dashboard', label: 'Dashboard' },
    { to: '/classroom/library', label: 'My Library' },
    { to: '/classroom/students', label: 'Students' },
    { to: '/classroom/settings', label: 'Settings' },
  ];

  const classroomSelectOptions = useMemo((): ClassroomSelectorOption[] => {
    const mapped =
      classrooms?.map((item) => ({
        title: item.name,
        value: item.id,
      })) || [];
    if (activeClassroom?.id && !mapped.some((o) => o.value === activeClassroom.id)) {
      return [{ title: activeClassroom.name || 'Classroom', value: activeClassroom.id }, ...mapped];
    }
    return mapped;
  }, [classrooms, activeClassroom]);

  return (
    <div className="sticky top-0 bg-gradient-to-r from-storyblueSecondLightText to-storyblueButton text-white p-2.5 shadow-md z-20">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 lg:gap-0">
        <div className="flex items-center w-full lg:w-auto">
          <div className="flex items-center sm:space-x-2 md:space-x-6 lg:space-x-20 w-full">
            <div className="flex items-center w-full sm:w-auto">
              <div className="hidden md:flex items-center flex-nowrap whitespace-nowrap font-bold pr-1.5 text-sm sm:text-base">
                Classroom -
              </div>
              <ClassroomSelector
                placeholder="Choose a classroom"
                addNewItemTitle="Add new classroom"
                addNewItemHandler={() => setShowAddNewClassroom(true)}
                selectedItem={activeClassroom?.id || null}
                onChange={(data: ClassroomSelectorOption) => selectActiveClassroom(data?.value)}
                options={classroomSelectOptions}
              ></ClassroomSelector>
            </div>

            {/* Mobile Menu Button - Only visible on mobile */}
            <div className="flex sm:hidden items-center ml-auto">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`bg-white bg-opacity-20 hover:bg-opacity-25 text-white p-2 rounded-lg ${isMobileMenuOpen ? 'opened' : 'closed'}`}
                id="mobile-teacher-navigation-menu-button"
                aria-label="Toggle navigation menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>

            {/* Desktop Navigation Links - Hidden on mobile */}
            <div className="hidden sm:flex flex-wrap gap-2 md:gap-6 lg:gap-8 w-full sm:w-auto">
              {navigationLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`teacher-tour-main-navigation-${link.label?.toLowerCase().replace(`'`, '').replace(' ', '-')} bg-white ${isActiveRoute(link.to) ? 'bg-opacity-15' : 'bg-opacity-0'} hover:bg-opacity-10 text-white font-bold text-sm sm:text-lg leading-none py-2 px-2 sm:px-4 rounded-full flex justify-between items-center text-nowrap`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu - Only visible when open on mobile  ---  */}
      <div className={`${isMobileMenuOpen ? 'block sm:hidden' : 'hidden sm:hidden'} bg-white bg-opacity-10 backdrop-blur-sm mt-2 rounded-lg`}>
        <div className="flex flex-col space-y-1 p-3">
          {navigationLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`mobile-teacher-tour-main-navigation-${link.label?.toLowerCase().replace(`'`, '').replace(' ', '-')} bg-white ${isActiveRoute(link.to) ? 'bg-opacity-20' : 'bg-opacity-5'} hover:bg-opacity-15 text-white font-bold text-base leading-none py-3 px-4 rounded-lg flex justify-center items-center transition-all duration-200`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>

      {showAddNewClassroom && (
        <ClassroomSetupWizard
          isOpen={true}
          isFirstClassroom={classrooms?.length === 0}
          onClose={() => setShowAddNewClassroom(false)}
          onCreateComplete={() => {
            setShowAddNewClassroom(false);
            navigateTo('/classroom/students?onboarding=true&addStartDate=true');
            dispatch(setShowAddNewStudentsPopup(true));
            dispatch(setNewClassroomFlow(true));
          }}
        />
      )}
      {!!showAddNewStudentsPopup && currentPathname?.includes('classroom/dashboard') && (
        <Modal
          // title={`Add students`}
          // description={`Add students, each on a new line. You dont need to use their full name, as long as you can tell them apart. We don't share this list with anyone.`}
          className={'global-absolute-popup-style font-sofiaSans secondary-small'}
          mainContainerClassName={'sm:max-w-[475px]'}
          descriptionClassContainer={'md:w-full'}
          descriptionClassName={'text-storyblackText font-sofiaSans font-medium'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={!isImportingStudents}
          showCloseButtonFunction={() => dispatch(setShowAddNewStudentsPopup(false))}
        >
          <StudentsAddNewItems
            loader={addNewStudentsLoader}
            isOnboarding={isOnboarding}
            limitClassroomStudents={currentStudentsLimit}
            skip={() => {
              createNewStudents([], true);
              dispatch(setShowAddNewStudentsPopup(false));
              if (isOnboarding) {
                navigateTo(`/classroom/students?onboarding=true&showCelebration=true`);
              } else {
                navigateTo(`/classroom/students?onboarding=true`);
              }
              setShowCelebrationPopup(true);
            }}
            back={() => window.history.back()}
            close={() => {
              dispatch(setShowAddNewStudentsPopup(false));
            }}
            submit={(names: string[]) => createNewStudents(names)}
            limitClassroomStudentsFeedback={() => setShowStudentLimitPopup(true)}
            classroomId={activeClassroom?.id}
            projectId={activeClassroomProject?.id}
            onStudentsImported={() => fetchStudents()}
            onImportingFlow={(isImporting) => setIsImportingStudents(isImporting)}
          ></StudentsAddNewItems>
        </Modal>
      )}
      {showCelebrationPopup && (
        <Modal title={''} className={'global-absolute-popup-style'} showSubmitButton={false} showCloseButton={false}>
          <OnboardingSuccess
            submit={() => {
              navigateTo(`/classroom/dashboard`);
              setShowCelebrationPopup(false);
              finishOnboarding();
            }}
          ></OnboardingSuccess>
        </Modal>
      )}
      <InformNotificationPopup
        isOpen={showStudentLimitPopup}
        onClose={() => setShowStudentLimitPopup(false)}
        title={'Enterprise'}
      >
        <div className="flex flex-col text-storyblackText font-sofiaSans md:max-w-md">
          <p className="mb-6">
            If you would like more than {limitClassroomStudents} student’s please contact us for a free enterprise
            account.
          </p>
          <div className="flex items-end">
            <div className="flex flex-col">
              <span>Email us at:</span>
              <span className="font-black">{CONFIG.ADMIN_EMAIL_ADDRESS}</span>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(CONFIG.ADMIN_EMAIL_ADDRESS).then(() => {
                  Toast('Email copied successfully', false);
                });
              }}
              aria-label="Copy"
              title="Copy email address"
              className="hover:opacity-80 transition-opacity bg-[#f5f5f4] rounded-lg p-1 ml-4"
            >
              <img src="/controls/copy-text-icon.png" alt="" className="w-6" />
            </button>
          </div>
        </div>
      </InformNotificationPopup>
    </div>
  );
};

export default MainTeacherControlBar;
